import React, { useEffect, useRef, useState } from 'react';
import { Camera, Image, Minus, Plus, Save, Trash2 } from 'lucide-react';
import { acquireHaccpTemperaturePhoto } from './useHaccpConfigurations';
import { temperatureIsCompliant } from './temperatureLimits';

function expectedTemperatureLabel(rule) {
  const minimum = rule.operational_target_min_temperature_c
    ?? rule.minimum_temperature_c ?? rule.min_temperature_c;
  const maximum = rule.operational_target_max_temperature_c
    ?? rule.maximum_temperature_c ?? rule.max_temperature_c;
  const format = (value) => {
    const numeric = Number(String(value).replace(',', '.'));
    if (!Number.isFinite(numeric)) return String(value);
    const magnitude = Math.abs(numeric);
    const text = Number.isInteger(magnitude) ? String(magnitude) : magnitude.toString();
    return numeric < 0 ? `−${text}` : numeric > 0 ? `+${text}` : '0';
  };
  if (minimum != null && maximum != null) {
    return `Valori previsti: ${format(minimum)}° / ${format(maximum)}°C`;
  }
  if (rule.limit_mode === 'MAX_ONLY' && maximum != null) return `Valore previsto: non oltre ${format(maximum)}°C`;
  if (rule.limit_mode === 'MIN_ONLY' && minimum != null) return `Valore previsto: almeno ${format(minimum)}°C`;
  return 'Valori previsti secondo il piano del locale.';
}

export default function TemperatureCaptureForm({ item, summary, saving, error, onCancel, onSubmit }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState('');
  const [capture, setCapture] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [temperature, setTemperature] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [note, setNote] = useState('');
  const [action, setAction] = useState('');
  const cameraInput = useRef(null);
  const libraryInput = useRef(null);
  const requestId = useRef(crypto.randomUUID());
  const confirmationId = useRef(crypto.randomUUID());
  const [pendingPayload, setPendingPayload] = useState(null);
  const numeric = Number(temperature.replace(',', '.'));
  const outside = temperature !== '' && Number.isFinite(numeric)
    && !temperatureIsCompliant(numeric, item);
  const probe = item.measurement_method === 'PROBE';

  useEffect(() => {
    if (!file) { setPreview(''); return undefined; }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const openPicker = (input) => {
    if (saving || uploading || pendingPayload || capture) return;
    if (input.current) {
      input.current.value = '';
      input.current.click();
    }
  };
  const selectPhoto = (event) => {
    const nextFile = event.target.files?.[0];
    if (!nextFile) return;
    setFile(nextFile);
    setCapture(null);
    setUploadError('');
    requestId.current = crypto.randomUUID();
    confirmationId.current = crypto.randomUUID();
    setPendingPayload(null);
  };
  const removePhoto = () => {
    if (saving || uploading || pendingPayload) return;
    setFile(null);
    setCapture(null);
    setUploadError('');
    requestId.current = crypto.randomUUID();
    confirmationId.current = crypto.randomUUID();
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!confirmed || !temperature.trim() || !Number.isFinite(numeric)
        || saving || uploading) return;

    let readyCapture = capture;
    if (file && !readyCapture) {
      setUploading(true);
      setUploadError('');
      try {
        readyCapture = await acquireHaccpTemperaturePhoto(
          summary.location_id,
          item.standalone ? undefined : item.equipment_id,
          file,
          requestId.current,
          item.standalone ? {
            station_id: item.station_id,
            profile_revision_id: item.profile_revision_id,
          } : null,
        );
        if (readyCapture?.state !== 'READY') throw new Error('PHOTO_NOT_READY');
        setCapture(readyCapture);
      } catch {
        setUploadError('Foto non allegata. Il controllo non è stato registrato: riprova oppure rimuovi la foto e continua senza allegato.');
        return;
      } finally {
        setUploading(false);
      }
    }

    const payload = {
      location_id: summary.location_id,
      ...(item.standalone
        ? {
          station_id: item.station_id,
          profile_revision_id: item.profile_revision_id,
        }
        : {
          equipment_id: item.equipment_id,
          scheduled_time_local: item.scheduled_time_local,
        }),
      input_method: readyCapture ? 'PHOTO' : 'MANUAL',
      ...(readyCapture ? { capture_id: readyCapture.id } : {}),
      temperature_c: temperature.replace(',', '.'),
      confirmed: true,
      probe_measured: probe,
      note: note.trim() || null,
      ...(item.standalone ? {} : {
        corrective_action: outside ? action.trim() || null : null,
      }),
      idempotency_key: confirmationId.current,
      verification_of: item.verification_of || null,
    };
    // Keep the exact same measurement, optional photo, and idempotency key on retry.
    const request = pendingPayload || payload;
    setPendingPayload(request);
    const result = await onSubmit(request);
    if (result === 'DEFINITIVE_ERROR') {
      setPendingPayload(null);
      confirmationId.current = crypto.randomUUID();
    }
  };

  return (
    <form
      className="haccp-temperature-form"
      data-testid="temperature-capture-form"
      onSubmit={submit}
    >
      <p className="haccp-temperature-form-limits">{expectedTemperatureLabel(item)}</p>
      <p className="haccp-temperature-instructions">
        {probe
          ? 'Inserisci il valore rilevato con la sonda prevista dal piano.'
          : 'Inserisci manualmente la temperatura letta sul display.'}
      </p>
      {item.instructions && <p className="haccp-temperature-instructions">{item.instructions}</p>}

      <div className="haccp-temperature-value-editor">
        <button
          type="button"
          className="haccp-button haccp-button-secondary"
          aria-label="Diminuisci di 0,1 °C"
          disabled={!temperature.trim() || !Number.isFinite(numeric) || saving || uploading || !!pendingPayload}
          onClick={() => {
            const adjusted = Math.round((numeric - 0.1) * 10) / 10;
            setTemperature(adjusted.toFixed(1));
            setConfirmed(false);
          }}
        ><Minus size={16} /></button>
        <label className="haccp-field">
          <span>{probe ? 'Valore della sonda °C' : 'Temperatura rilevata °C'}</span>
          <input
            aria-label="Temperatura rilevata"
            inputMode="decimal"
            required
            pattern="[+-]?[0-9]+([.,][0-9]{1,2})?"
            value={temperature}
            disabled={saving || uploading || !!pendingPayload}
            onChange={(event) => {
              setTemperature(event.target.value);
              setConfirmed(false);
            }}
          />
        </label>
        <button
          type="button"
          className="haccp-button haccp-button-secondary"
          aria-label="Aumenta di 0,1 °C"
          disabled={!temperature.trim() || !Number.isFinite(numeric) || saving || uploading || !!pendingPayload}
          onClick={() => {
            const adjusted = Math.round((numeric + 0.1) * 10) / 10;
            setTemperature(adjusted.toFixed(1));
            setConfirmed(false);
          }}
        ><Plus size={16} /></button>
      </div>

      <div className="haccp-temperature-input-methods" role="group" aria-label="Foto facoltativa">
        <button
          type="button"
          className="haccp-button haccp-button-secondary"
          disabled={saving || uploading || !!pendingPayload || Boolean(capture)}
          onClick={() => openPicker(cameraInput)}
        ><Camera size={16} /> Fai foto</button>
        <button
          type="button"
          className="haccp-button haccp-button-secondary"
          disabled={saving || uploading || !!pendingPayload || Boolean(capture)}
          onClick={() => openPicker(libraryInput)}
        ><Image size={16} /> Scegli una foto</button>
      </div>
      <input
        ref={cameraInput}
        className="haccp-temperature-file-input"
        type="file"
        accept="image/jpeg,image/png"
        capture="environment"
        aria-label="Scatta foto al display"
        disabled={saving || uploading || !!pendingPayload || Boolean(capture)}
        onChange={selectPhoto}
      />
      <input
        ref={libraryInput}
        className="haccp-temperature-file-input"
        type="file"
        accept="image/jpeg,image/png"
        aria-label="Scegli foto dalla libreria"
        disabled={saving || uploading || !!pendingPayload || Boolean(capture)}
        onChange={selectPhoto}
      />
      {file && preview && (
        <div className="haccp-temperature-photo-selection">
          <img
            className="haccp-temperature-photo"
            src={preview}
            alt={`Foto display ${item.equipment_kind} ${item.equipment_number}`}
          />
          <button
            type="button"
            className="haccp-button haccp-button-secondary"
            disabled={saving || uploading || !!pendingPayload}
            onClick={removePhoto}
          ><Trash2 size={16} /> Rimuovi foto</button>
          {capture && <small>Foto originale privata allegata al controllo.</small>}
        </div>
      )}
      {uploadError && <p role="alert" className="haccp-temperature-error">{uploadError}</p>}

      {outside && <>
        <p className="haccp-temperature-error">
          {item.standalone
            ? 'Valore fuori dai limiti configurati. Registra una nota per lo storico.'
            : 'Fuori limite: si aprirà una non conformità con controllo successivo.'}
        </p>
        {!item.standalone && <p className="haccp-temperature-instructions">{item.corrective_instructions}</p>}
        {item.anomaly_note_required && (
          <label className="haccp-field">
            <span>Nota anomalia</span>
            <textarea
              required
              maxLength={1000}
              value={note}
              disabled={saving || uploading || !!pendingPayload}
              onChange={(event) => setNote(event.target.value)}
            />
          </label>
        )}
        {!item.standalone && (
          <label className="haccp-field">
            <span>Azione correttiva eseguita</span>
            <textarea
              aria-label="Azione correttiva"
              required
              maxLength={1000}
              value={action}
              disabled={saving || uploading || !!pendingPayload}
              onChange={(event) => setAction(event.target.value)}
            />
          </label>
        )}
      </>}

      <label className="haccp-temperature-checkline">
        <input
          type="checkbox"
          required
          checked={confirmed}
          disabled={saving || uploading || !!pendingPayload}
          onChange={(event) => setConfirmed(event.target.checked)}
        />
        Confermo la temperatura inserita manualmente.
      </label>
      {error && <p className="haccp-temperature-error" role="alert">{error}</p>}
      <div className="haccp-temperature-form-actions">
        <button
          type="button"
          className="haccp-button haccp-button-secondary"
          onClick={onCancel}
          disabled={saving || uploading}
        >Chiudi</button>
        <button
          type="submit"
          className="haccp-button haccp-button-primary"
          disabled={!temperature.trim() || !Number.isFinite(numeric) || !confirmed || saving || uploading}
        >
          <Save size={15} />
          {uploading
            ? 'Allegando foto…'
            : saving
              ? 'Salvataggio…'
              : uploadError && file
                ? 'Riprova foto e conferma'
                : pendingPayload
                  ? 'Riprova conferma'
                  : 'Conferma'}
        </button>
      </div>
    </form>
  );
}
